import { Injectable, Injector } from '@angular/core';
import { SapInvoiceDto, SapInvoiceServiceProxy, SearchInvoicesInput, SearchInvoicesOutput, GetEmploymentDto, SapCancellationReasonDto, SapProductDto, SearchProductsInput, SearchProductsOutput, SearchCancellationReasonsOutput, SearchCancellationReasonsInput, SapPersonDto, InvoiceAddressDto, ContactDto, SapSponsorshipDto, SapBusinessDto, PersonLicenceType, CreateOrUpdateSapInvoiceDto, SponsorshipInvoiceAddressDto, SearchContactPersonsOutput, SearchContactPersonsInput, SapContactPersonDto } from '@shared/service-proxies/service-proxies';
import { PersonDossierService } from './person-dossier.service';
import { tap, Observable } from 'rxjs';
import { SelectItem } from 'primeng/api';
import { AppComponentBase } from '../../../shared/common/app-component-base';
import { AppConsts } from '../../../shared/AppConsts';
import { SponsorshipDossierService } from './sponsorship-dossier.service';

@Injectable({
    providedIn: 'root',
})
export class InvoiceService extends AppComponentBase {

    public allProducts: SapProductDto[] = [];
    public allContactPersons: SapContactPersonDto[] = [];

    constructor(
        injector: Injector,
        private invoiceService: SapInvoiceServiceProxy,
        private personDossierService: PersonDossierService,
        private sponsorshipDossierService: SponsorshipDossierService
    ) {
        super(injector);
    }

    public GetInvoices(sorting: string, filter: string, skipCount: number, maxCount: number): Observable<SearchInvoicesOutput> {
        const input = new SearchInvoicesInput();
        input.filter = filter;
        input.sorting = sorting;
        input.maxResultCount = maxCount;
        input.skipCount = skipCount;
        return this.invoiceService.search(input);
    }

    public exportToExcel(sorting: string, filter: string): Observable<string> {
        return this.invoiceService.exportToExcel(filter, sorting);
    }

    public GetInvoicesForPersonOrSponsorship(sorting: string, filter: string, skipCount: number, maxCount: number): Observable<SearchInvoicesOutput> {
        const input = new SearchInvoicesInput();
        input.personId = this.personDossierService.GetId();
        input.sponsorshipId = this.sponsorshipDossierService.GetId();
        input.filter = filter;
        input.sorting = sorting;
        input.maxResultCount = maxCount;
        input.skipCount = skipCount;
        return this.invoiceService.search(input);
    }

    public GetInvoicesForPersonOrSponsorshipCount(sorting: string, filter: string, skipCount: number, maxCount: number, personId: number, sponsorshipId: number): Observable<SearchInvoicesOutput> {
        const input = new SearchInvoicesInput();
        input.personId = personId;
        input.sponsorshipId = sponsorshipId;
        input.filter = filter;
        input.sorting = sorting;
        input.maxResultCount = maxCount;
        input.skipCount = skipCount;
        return this.invoiceService.search(input);
    }

    public getInvoice(id: number): Observable<SapInvoiceDto> {
        return this.invoiceService.get(id);
    }

    public getEmptyInvoice(personId: number, sponsorshipId: number): Observable<SapInvoiceDto> {
        return this.invoiceService.getEmpty(personId, sponsorshipId);
    }

    public createInvoice(dto: CreateOrUpdateSapInvoiceDto): Observable<SapInvoiceDto> {
        return this.invoiceService.create(dto);
    }

    public updateInvoice(invoiceId: number, dto: CreateOrUpdateSapInvoiceDto): Observable<SapInvoiceDto> {
        return this.invoiceService.update(invoiceId, dto);
    }

    public releaseInvoice(invoiceId: number): Observable<SapInvoiceDto> {
        return this.invoiceService.releaseInvoice(invoiceId);
    }

    public cancelInvoice(invoiceId: number, cancellationReasonId: number): Observable<void> {
        return this.invoiceService.cancelInvoice(invoiceId, cancellationReasonId);
    }

    public deleteInvoice(id: number, reason: string): Observable<void> {
        return this.invoiceService.delete(id, reason);
    }

    public createInvoiceForPersonDisciplinaryMeasures(id: number): Observable<number> {
        return this.invoiceService.createInvoiceForPersonDisciplinaryMeasures(id);
    }

    public createInvoiceForPersonLicence(id: number): Observable<number> {
        return this.invoiceService.createInvoiceForPersonLicence(id);
    }

    public createInvoiceForPersonLicenceRenewal(id: number): Observable<number> {
        return this.invoiceService.createInvoiceForPersonLicenceRenewal(id);
    }

    public createInvoiceForNarcoticPrescriptionBlock(id: number): Observable<number> {
        return this.invoiceService.createInvoiceForNarcoticPrescriptionBlock(id);
    }

    public createInvoiceForInspection(id: number): Observable<number> {
        return this.invoiceService.createInvoiceForInspection(id);
    }

    public createInvoiceForCorporateLicence(id: number): Observable<number> {
        return this.invoiceService.createInvoiceForCorporateLicence(id);
    }

    public createInvoiceForCorporateLicenceRenewal(id: number): Observable<number> {
        return this.invoiceService.createInvoiceForCorporateLicenceRenewal(id);
    }

    // These offset are used to avoid id conflicts where persons, businesses and sponsorships are included in the same dropdown. Businesses have no offset.
    public PersonIdOffset = 10000000;
    public SponsorshipIdOffset = 20000000;

    createPersonBillingAddressDropdownList(person: SapPersonDto, employments: GetEmploymentDto[]): SelectItem[] {
        let dropdownList: SelectItem[] = [];

        if (person?.invoiceAddress) {
            dropdownList.push({ value: AppConsts.codes.invoiceAddressType.invoice, label: this.l('InvoiceAddressType-Invoice') + ': ' + this.getPersonInvoiceAddressDescription(person.invoiceAddress) });
        }

        if (person?.contact) {
            dropdownList.push({ value: AppConsts.codes.invoiceAddressType.private, label: this.l('InvoiceAddressType-Private') + ': ' + this.getPrivateAddressDescription(person.contact) });
        }

        employments.forEach(employment => {
            if ((employment.personLicence?.discriminator === PersonLicenceType.ProfessionalLicence || employment.personLicence?.discriminator === PersonLicenceType.ProfessionalNinetyDays) && !!employment.businessId) {
                dropdownList.push({ value: employment.businessId, label: this.getEmploymentAddressBusinessDescription(employment) });
            }

            if ((employment.personLicence?.discriminator === PersonLicenceType.ProfessionalLicence || employment.personLicence?.discriminator === PersonLicenceType.ProfessionalNinetyDays) && !!employment.sponsorshipId) {
                dropdownList.push({ value: employment.sponsorshipId + this.SponsorshipIdOffset, label: this.getEmploymentAddressSponsorshipDescription(employment) });
            }
        });

        return dropdownList;
    }

    createSponsorshipBillingAddressDropdownList(sponsorship: SapSponsorshipDto, businesses: SapBusinessDto[]): SelectItem[] {
        let dropdownList: SelectItem[] = [];

        if (sponsorship?.invoiceAddress) {
            dropdownList.push({ value: AppConsts.codes.invoiceAddressType.invoice, label: this.l('InvoiceAddressType-Invoice') + ': ' + this.getSponsorshipInvoiceAddressDescription(sponsorship.invoiceAddress) });
        }

        if (sponsorship?.street) {
            dropdownList.push({ value: AppConsts.codes.invoiceAddressType.private, label: this.l('SponsorshipAddress') + ': ' + this.getSponsorshipAddressDescription(sponsorship) });
        }

        businesses.forEach(business => {
            dropdownList.push({ value: business.id, label: this.getBusinessAddressDescription(business) });
        });

        return dropdownList;
    }

    createAlternateBillingAddressDropdownList(employments: GetEmploymentDto[]): SelectItem[] {
        let dropdownList: SelectItem[] = [];

        employments.forEach((employment: GetEmploymentDto) => {
            if (!(employment.personLicence?.discriminator === PersonLicenceType.ProfessionalLicence || employment.personLicence?.discriminator === PersonLicenceType.ProfessionalNinetyDays) && !!employment.businessId) {
                dropdownList.push({ value: employment.businessId, label: this.getEmploymentAddressBusinessDescription(employment) });
            }

            if (!(employment.personLicence?.discriminator === PersonLicenceType.ProfessionalLicence || employment.personLicence?.discriminator === PersonLicenceType.ProfessionalNinetyDays) && !!employment.sponsorshipId) {
                dropdownList.push({ value: employment.sponsorshipId + this.SponsorshipIdOffset, label: this.getEmploymentAddressSponsorshipDescription(employment) });
            }
        });

        return dropdownList;
    }

    createProductsDropdownList(products: SapProductDto[], cantonalDepartmentId: string, forPerson: boolean): SelectItem[] {
        let dropdownList: SelectItem[] = [];

        if (cantonalDepartmentId) {
            if (forPerson) {
                products.filter(x => x.cantonalDepartmentId === cantonalDepartmentId && x.contextId !== AppConsts.codes.invoiceContext.corporateLicence).forEach(product => {
                    dropdownList.push({ value: product.id, label: this.createProductLabel(product) });
                });
            } else {
                products.filter(x => x.cantonalDepartmentId === cantonalDepartmentId && x.contextId !== AppConsts.codes.invoiceContext.professionalLicence).forEach(product => {
                    dropdownList.push({ value: product.id, label: this.createProductLabel(product) });
                });
            }
        }

        return dropdownList;
    }

    createProductLabel(product: SapProductDto): string {
        return `${product.sapId}: ${product.sapDescription}${product.detailedDescription ? ' ' + product.detailedDescription : ''}, CHF:${product.pricePerUnit?.toFixed(2)}`;
    }

    createContactPersonsDropdownList(contactPersons: SapContactPersonDto[], cantonalDepartmentId: string, costCenterId: string): SelectItem[] {
        let dropdownList: SelectItem[] = [];

        contactPersons.filter(x => x.cantonalDepartmentId === cantonalDepartmentId && x.costCenterId === costCenterId).forEach(contactPerson => {
            dropdownList.push({ value: contactPerson.id, label: this.createContactPersonLabel(contactPerson) });
        });

        return dropdownList;
    }

    createContactPersonLabel(contactPerson: SapContactPersonDto): string {
        return `${contactPerson.abbreviation} (${contactPerson.description})`;
    }

    createCancellationReasonsDropdownList(reasons: SapCancellationReasonDto[]): SelectItem[] {
        let dropdownList: SelectItem[] = [];

        reasons.forEach(reason => {
            dropdownList.push({ value: reason.id, label: reason.sapDescription });
        });

        return dropdownList;
    }

    private getPrivateAddressDescription(contact: ContactDto) {
        if (!contact) {
            return '';
        }

        let str = contact.street ?? '';

        if (contact.streetNr?.length > 0) {
            str =  str.length > 0 ? str.concat(' ', contact.streetNr) : str.concat(contact.streetNr);
        }

        if (contact.postCode?.length > 0) {
            str = str.length > 0 ? str.concat(', ', contact.postCode) : str.concat(contact.postCode);
        }

        if (contact.city?.length > 0) {
            str = str.length > 0 ? str.concat(' ', contact.city) : str.concat(contact.city);
        }

        return str;
    }

    private getSponsorshipAddressDescription(sponsorship: SapSponsorshipDto) {
        if (!sponsorship) {
            return '';
        }

        let str = sponsorship.street ?? '';

        if (sponsorship.streetNr?.length > 0) {
            str =  str.length > 0 ? str.concat(' ', sponsorship.streetNr) : str.concat(sponsorship.streetNr);
        }

        if (sponsorship.postCode?.length > 0) {
            str = str.length > 0 ? str.concat(', ', sponsorship.postCode) : str.concat(sponsorship.postCode);
        }

        if (sponsorship.city?.length > 0) {
            str = str.length > 0 ? str.concat(' ', sponsorship.city) : str.concat(sponsorship.city);
        }

        return str;
    }

    private getPersonInvoiceAddressDescription(invoiceAddress: InvoiceAddressDto) {
        if (!invoiceAddress) {
            return '';
        }

        let str = '';
        if (!!invoiceAddress.name) {
            str = str.concat(invoiceAddress.name, ', ');
        }

        str = str.concat(invoiceAddress.street ?? '');

        if (invoiceAddress.streetNr?.length > 0) {
            str =  str.length > 0 ? str.concat(' ', invoiceAddress.streetNr) : str.concat(invoiceAddress.streetNr);
        }

        if (invoiceAddress.postCode?.length > 0) {
            str = str.length > 0 ? str.concat(', ', invoiceAddress.postCode) : str.concat(invoiceAddress.postCode);
        }

        if (invoiceAddress.city?.length > 0) {
            str = str.length > 0 ? str.concat(' ', invoiceAddress.city) : str.concat(invoiceAddress.city);
        }

        return str;
    }

    private getSponsorshipInvoiceAddressDescription(invoiceAddress: SponsorshipInvoiceAddressDto) {
        if (!invoiceAddress) {
            return '';
        }

        let str = '';
        if (!!invoiceAddress.name) {
            str = str.concat(invoiceAddress.name, ', ');
        }

        str = str.concat(invoiceAddress.street ?? '');

        if (invoiceAddress.streetNr?.length > 0) {
            str =  str.length > 0 ? str.concat(' ', invoiceAddress.streetNr) : str.concat(invoiceAddress.streetNr);
        }

        if (invoiceAddress.postCode?.length > 0) {
            str = str.length > 0 ? str.concat(', ', invoiceAddress.postCode) : str.concat(invoiceAddress.postCode);
        }

        if (invoiceAddress.city?.length > 0) {
            str = str.length > 0 ? str.concat(' ', invoiceAddress.city) : str.concat(invoiceAddress.city);
        }

        return str;
    }

    private getEmploymentAddressBusinessDescription(employment: GetEmploymentDto): string {

        let str = employment.business.name ?? '';

        if (employment.business.street?.length > 0) {
            if (str.length > 0) {
                str = str.concat(', ');
            }

            str = str.concat(employment.business.street);

            if (!!employment.business.streetNr && employment.business.streetNr.length > 0) {
                str = str.concat(' ');
                str = str.concat(employment.business.streetNr);
            }
        }

        if (!!employment.business.postCode && employment.business.postCode.length > 0) {
            if (str.length > 0) {
                str = str.concat(', ');
            }

            str = str.concat(employment.business.postCode);
        }

        if (!!employment.business.city && employment.business.city.length > 0) {
            if (str.length > 0) {
                str = str.concat(' ');
            }

            str = str.concat(employment.business.city);
        }

        if (employment.personLicence) {
            return str + ` (Lizenz: ${this.l('PersonLicenceType_' + PersonLicenceType[employment.personLicence.discriminator] + '-Short')})`;
        } else {
            return str;
        }
    }

    private getEmploymentAddressSponsorshipDescription(employment: GetEmploymentDto): string {
        if (!!employment?.sponsorship) {
            return '';
        }

        let str = employment.sponsorship.name ?? '';

        if (employment.sponsorship.street?.length > 0) {
            str =  str.length > 0 ? str.concat(', ', employment.sponsorship.street) : str.concat(employment.sponsorship.street);
        }

        if (employment.sponsorship.streetNr?.length > 0) {
            str =  str.length > 0 ? str.concat(' ', employment.sponsorship.streetNr) : str.concat(employment.sponsorship.streetNr);
        }

        if (employment.sponsorship.postCode?.length > 0) {
            str = str.length > 0 ? str.concat(', ', employment.sponsorship.postCode) : str.concat(employment.sponsorship.postCode);
        }

        if (employment.sponsorship.city?.length > 0) {
            str = str.length > 0 ? str.concat(' ', employment.sponsorship.city) : str.concat(employment.sponsorship.city);
        }

        if (employment.personLicence) {
            return str + ` (Lizenz: ${this.l('PersonLicenceType_' + PersonLicenceType[employment.personLicence.discriminator] + '-Short')})`;
        } else {
            return str;
        }
    }

    private getBusinessAddressDescription(business: SapBusinessDto): string {
        if (business == null) {
            return '';
        }

        let str = business.name ?? '';

        if (business.street?.length > 0) {
            str =  str.length > 0 ? str.concat(', ', business.street) : str.concat(business.street);
        }

        if (business.streetNr?.length > 0) {
            str =  str.length > 0 ? str.concat(' ', business.streetNr) : str.concat(business.streetNr);
        }

        if (business.postCode?.length > 0) {
            str = str.length > 0 ? str.concat(', ', business.postCode) : str.concat(business.postCode);
        }

        if (business.city?.length > 0) {
            str = str.length > 0 ? str.concat(' ', business.city) : str.concat(business.city);
        }

        return str;
    }

    public searchProducts(sorting: string, filter: string, skipCount: number, maxCount: number): Observable<SearchProductsOutput> {
        const input = new SearchProductsInput();
        input.filter = filter;
        input.sorting = sorting;
        input.maxResultCount = maxCount;
        input.skipCount = skipCount;
        return this.invoiceService.searchProducts(input);
    }

    public getProducts(): Observable<SapProductDto[]> {
        return this.invoiceService.getProducts(true).pipe(tap((products: SapProductDto[]) => {
            this.allProducts = products;
        }));
    }

    public getProduct(id: number): Observable<SapProductDto> {
        return this.invoiceService.getProduct(id);
    }

    public createOrUpdateProduct(dto: SapProductDto): Observable<SapProductDto> {
        return this.invoiceService.createOrUpdateProduct(dto).pipe(tap((_: SapProductDto) => {
            this.getProducts().subscribe();
        }));
    }

    public deleteProduct(id: number): Observable<void> {
        return this.invoiceService.deleteProduct(id).pipe(tap(() => {
            this.getProducts().subscribe();
        }));
    }

    public searchCancellationReasons(sorting: string, filter: string, skipCount: number, maxCount: number): Observable<SearchCancellationReasonsOutput> {
        const input = new SearchCancellationReasonsInput();
        input.filter = filter;
        input.sorting = sorting;
        input.maxResultCount = maxCount;
        input.skipCount = skipCount;
        return this.invoiceService.searchCancellationReasons(input);
    }

    public getCancellationReasons(): Observable<SapCancellationReasonDto[]> {
        return this.invoiceService.getCancellationReasons();
    }

    public getCancellationReason(id: number): Observable<SapCancellationReasonDto> {
        return this.invoiceService.getCancellationReason(id);
    }

    public createOrUpdateCancellationReason(dto: SapCancellationReasonDto): Observable<SapCancellationReasonDto> {
        return this.invoiceService.createOrUpdateCancellationReason(dto);
    }

    public deleteCancellationReason(id: number): Observable<void> {
        return this.invoiceService.deleteCancellationReason(id);
    }

    public searchContactPersons(sorting: string, filter: string, skipCount: number, maxCount: number): Observable<SearchContactPersonsOutput> {
        const input = new SearchContactPersonsInput();
        input.filter = filter;
        input.sorting = sorting;
        input.maxResultCount = maxCount;
        input.skipCount = skipCount;
        return this.invoiceService.searchContactPersons(input);
    }

    public getContactPersons(): Observable<SapContactPersonDto[]> {
        return this.invoiceService.getContactPersons(true).pipe(tap((contactPersons: SapContactPersonDto[]) => {
            this.allContactPersons = contactPersons;
        }));
    }

    public getContactPerson(id: number): Observable<SapContactPersonDto> {
        return this.invoiceService.getContactPerson(id);
    }

    public createOrUpdateContactPerson(dto: SapContactPersonDto): Observable<SapContactPersonDto> {
        return this.invoiceService.createOrUpdateContactPerson(dto).pipe(tap((_: SapContactPersonDto) => {
            this.getContactPersons().subscribe();
        }));
    }

    public deleteContactPerson(id: number): Observable<void> {
        return this.invoiceService.deleteContactPerson(id).pipe(tap(() => {
            this.getContactPersons().subscribe();
        }));
    }
}
